import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, Plus, KeyRound, X, MailPlus, Copy, RotateCw, Clock } from "lucide-react";

const ROLES = [
  { value: "super_admin", label: "Super Admin" },
  { value: "marketing_manager", label: "Marketing Manager" },
  { value: "staff", label: "DM Staff" },
  { value: "viewer", label: "Viewer" },
];

export default function Users() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [form, setForm] = useState({ name: "", email: "", role: "viewer" });
  const [lastInviteUrl, setLastInviteUrl] = useState(null);

  const load = async () => {
    const [u, i] = await Promise.all([api.get("/users"), api.get("/invites")]);
    setUsers(u.data);
    setInvites(i.data.invites || []);
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}/role`, { role });
      toast.success("Role diperbarui");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal update role"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Hapus ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User dihapus");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal hapus user"); }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/invites", form);
      const emStatus = data.email_result?.status;
      if (emStatus === "sent") {
        toast.success(`Invite terkirim ke ${form.email}`);
      } else if (emStatus === "skipped") {
        toast.warning("Invite dibuat tapi email tidak terkirim (RESEND_API_KEY kosong)");
      } else {
        toast.warning(`Invite dibuat. Email status: ${emStatus}`);
      }
      setLastInviteUrl(data.invite_url);
      setForm({ name: "", email: "", role: "viewer" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal buat invite"); }
  };

  const resendInvite = async (inv) => {
    try {
      const { data } = await api.post(`/invites/${inv.id}/resend`);
      const s = data.email_result?.status;
      toast.success(s === "sent" ? "Invite dikirim ulang" : `Invite direfresh (email: ${s})`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal resend"); }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke invite untuk ${inv.email}?`)) return;
    await api.delete(`/invites/${inv.id}`);
    toast.success("Invite dibatalkan");
    load();
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success("URL invite disalin");
  };

  const resetPw = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) return toast.error("Password minimal 6 karakter");
    try {
      await api.patch(`/users/${pwUser.id}/password`, { password: newPw });
      toast.success("Password direset");
      setPwUser(null); setNewPw("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal reset password"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 fade-in">
      <div className="pb-2 border-b border-[#1F1F1F] flex items-end justify-between">
        <div>
          <div className="label-mono">// access control · super admin only</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2" data-testid="users-title">
            Manajemen <span className="text-[#00F0FF]">Pengguna</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-2">User baru dibuat via email invite. Password diset sendiri oleh user via link setup.</p>
        </div>
        <button onClick={() => setShowInvite(true)}
          data-testid="users-invite-btn"
          className="bg-[#007AFF] hover:bg-[#0056b3] px-4 py-2.5 label-mono flex items-center gap-2 transition">
          <MailPlus size={12} /> undang pengguna
        </button>
      </div>

      <div className="flex items-center gap-1 border border-[#1F1F1F] bg-[#0A0A0A] p-1 w-fit">
        <button onClick={() => setTab("users")}
          data-testid="tab-users"
          className={`label-mono px-4 py-2 ${tab === "users" ? "bg-[#007AFF] text-white" : "text-zinc-500 hover:text-white"}`}>
          pengguna aktif ({users.length})
        </button>
        <button onClick={() => setTab("invites")}
          data-testid="tab-invites"
          className={`label-mono px-4 py-2 flex items-center gap-2 ${tab === "invites" ? "bg-[#007AFF] text-white" : "text-zinc-500 hover:text-white"}`}>
          <Clock size={10} /> pending invites ({invites.length})
        </button>
      </div>

      {tab === "users" && (
        <div className="border border-[#1F1F1F] bg-[#0A0A0A]">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="border-b border-[#1F1F1F]">
                <th className="label-mono text-left px-4 py-3">nama</th>
                <th className="label-mono text-left px-4 py-3">email</th>
                <th className="label-mono text-left px-4 py-3">role</th>
                <th className="label-mono text-left px-4 py-3">dibuat</th>
                <th className="label-mono text-right px-4 py-3">aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-[#1F1F1F] hover:bg-[#0F0F0F]" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 text-sm flex items-center gap-2">
                    <div className="w-7 h-7 bg-[#1F1F1F] flex items-center justify-center font-mono text-xs">{u.name[0]?.toUpperCase()}</div>
                    {u.name}
                    {u.id === me?.id && <span className="label-mono text-[#00F0FF] ml-1">(anda)</span>}
                    {u.via_invite && <span className="label-mono text-zinc-600 ml-1">via invite</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{u.email}</td>
                  <td className="px-4 py-3">
                    <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} disabled={u.id === me?.id}
                      data-testid={`user-role-${u.id}`}
                      className="bg-[#050505] border border-[#1F1F1F] px-2 py-1 font-mono text-xs outline-none focus:border-[#007AFF] disabled:opacity-40">
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{new Date(u.created_at).toLocaleDateString("id-ID")}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setPwUser(u)}
                      data-testid={`user-reset-pw-${u.id}`}
                      title="Reset password" className="text-zinc-500 hover:text-[#FFCC00] p-1 mr-1"><KeyRound size={14} /></button>
                    <button onClick={() => del(u)} disabled={u.id === me?.id}
                      data-testid={`user-delete-${u.id}`}
                      className="text-zinc-500 hover:text-[#FF3B30] p-1 disabled:opacity-30"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "invites" && (
        <div className="border border-[#1F1F1F] bg-[#0A0A0A]" data-testid="invites-table">
          {invites.length === 0 ? (
            <div className="p-10 text-center label-mono text-zinc-500">tidak ada invite pending</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1F1F1F]">
                  <th className="label-mono text-left px-4 py-3">nama</th>
                  <th className="label-mono text-left px-4 py-3">email</th>
                  <th className="label-mono text-left px-4 py-3">role</th>
                  <th className="label-mono text-left px-4 py-3">expire</th>
                  <th className="label-mono text-right px-4 py-3">aksi</th>
                </tr>
              </thead>
              <tbody>
                {invites.map(inv => (
                  <tr key={inv.id} className="border-b border-[#1F1F1F] hover:bg-[#0F0F0F]" data-testid={`invite-row-${inv.id}`}>
                    <td className="px-4 py-3 text-sm">{inv.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-400">{inv.email}</td>
                    <td className="px-4 py-3 label-mono text-[#00F0FF]">{ROLES.find(r => r.value === inv.role)?.label || inv.role}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">{new Date(inv.expires_at).toLocaleString("id-ID")}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => resendInvite(inv)} title="Resend email"
                        data-testid={`invite-resend-${inv.id}`}
                        className="text-zinc-500 hover:text-[#00F0FF] p-1 mr-1"><RotateCw size={14} /></button>
                      <button onClick={() => revokeInvite(inv)} title="Revoke invite"
                        data-testid={`invite-revoke-${inv.id}`}
                        className="text-zinc-500 hover:text-[#FF3B30] p-1"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => { setShowInvite(false); setLastInviteUrl(null); }}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-[#1F1F1F] bg-[#0A0A0A] p-6 space-y-4" data-testid="invite-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-mono">// send invite email</div>
                <h3 className="font-display font-bold text-2xl mt-1">Undang Pengguna Baru</h3>
              </div>
              <button onClick={() => { setShowInvite(false); setLastInviteUrl(null); }} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            {lastInviteUrl ? (
              <div className="space-y-3">
                <div className="border border-[#00E676]/40 border-l-4 border-l-[#00E676] bg-[#00E676]/5 p-3 label-mono text-[#00E676]">
                  ● invite berhasil dibuat & dikirim
                </div>
                <div className="label-mono">// fallback: bagikan link ini kalau email tidak sampai</div>
                <div className="flex items-center gap-2 bg-[#050505] border border-[#1F1F1F] p-3">
                  <code className="flex-1 text-xs text-[#00F0FF] break-all">{lastInviteUrl}</code>
                  <button onClick={() => copyUrl(lastInviteUrl)}
                    data-testid="copy-invite-url"
                    className="p-2 border border-[#1F1F1F] hover:border-[#007AFF]"><Copy size={12} /></button>
                </div>
                <button onClick={() => { setShowInvite(false); setLastInviteUrl(null); }}
                  className="w-full py-2.5 bg-[#007AFF] hover:bg-[#0056b3] label-mono">selesai</button>
              </div>
            ) : (
              <form onSubmit={sendInvite} className="space-y-4">
                <div>
                  <label className="label-mono block mb-2">nama lengkap</label>
                  <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                    data-testid="invite-form-name"
                    className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]" />
                </div>
                <div>
                  <label className="label-mono block mb-2">email</label>
                  <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                    data-testid="invite-form-email"
                    placeholder="user@perusahaan.co.id"
                    className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]" />
                </div>
                <div>
                  <label className="label-mono block mb-2">role</label>
                  <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
                    data-testid="invite-form-role"
                    className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]">
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="label-mono text-zinc-600">// sistem akan kirim email dengan link setup password (expire 48 jam)</div>
                <div className="flex items-center gap-3 pt-2">
                  <button type="button" onClick={() => setShowInvite(false)} className="flex-1 py-2.5 border border-[#1F1F1F] label-mono hover:bg-[#0F0F0F]">batal</button>
                  <button type="submit" data-testid="invite-form-submit"
                    className="flex-1 py-2.5 bg-[#007AFF] hover:bg-[#0056b3] label-mono">→ kirim invite</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {pwUser && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPwUser(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={resetPw}
            data-testid="reset-pw-modal"
            className="w-full max-w-sm border border-[#1F1F1F] bg-[#0A0A0A] p-6 space-y-4">
            <div>
              <div className="label-mono">// reset credential</div>
              <h3 className="font-display font-bold text-xl mt-1">Reset Password</h3>
              <p className="text-sm text-zinc-500 mt-1">Set password baru untuk <span className="text-white">{pwUser.name}</span></p>
            </div>
            <input type="password" required minLength={6} value={newPw} onChange={(e) => setNewPw(e.target.value)}
              placeholder="password baru (min. 6 karakter)"
              data-testid="reset-pw-input"
              autoFocus
              className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]" />
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => { setPwUser(null); setNewPw(""); }} className="flex-1 py-2.5 border border-[#1F1F1F] label-mono hover:bg-[#0F0F0F]">batal</button>
              <button type="submit" data-testid="reset-pw-submit"
                className="flex-1 py-2.5 bg-[#007AFF] hover:bg-[#0056b3] label-mono">→ reset</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
